/**
 * Tough-Bar Builder - Modals Module
 * All modal action methods (tablet, notify, etc.)
 */
window.ToughBarBuilder = window.ToughBarBuilder || {};

window.ToughBarBuilder.modals = {
  // Shared body scroll lock
  normalizeBodyScrollLockOwner(owner = 'modal') {
    const normalizedOwner = String(owner || '').trim().toLowerCase();
    return normalizedOwner || 'modal';
  },

  syncBodyScrollLockAttributes() {
    if (typeof document === 'undefined' || !document.body) return;

    const owners = this.bodyScrollLockOwners || {};
    if (owners.modal) document.body.setAttribute('data-scroll-locked', 'true');
    else document.body.removeAttribute('data-scroll-locked');

    if (owners['side-cart']) document.body.setAttribute('data-side-cart-locked', 'true');
    else document.body.removeAttribute('data-side-cart-locked');
  },

  applyBodyScrollLock(position) {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) return;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.cssText = `
      position: fixed !important;
      top: -${position}px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      overflow: hidden !important;
      padding-right: ${scrollbarWidth}px !important;
    `;

    document.documentElement.style.overflow = 'hidden';
  },

  acquireBodyScrollLock(owner = 'modal') {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const normalizedOwner = this.normalizeBodyScrollLockOwner(owner);
    const owners = { ...(this.bodyScrollLockOwners || {}) };
    if (owners[normalizedOwner]) {
      this.syncBodyScrollLockAttributes();
      return;
    }

    const hadActiveLock = Object.keys(owners).length > 0;
    owners[normalizedOwner] = true;
    this.bodyScrollLockOwners = owners;

    if (!hadActiveLock) {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      this.bodyScrollLockPosition = scrollPosition;
      this.applyBodyScrollLock(scrollPosition);
    }

    this.syncBodyScrollLockAttributes();
  },

  releaseBodyScrollLock(owner = 'modal') {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) return;

    const normalizedOwner = this.normalizeBodyScrollLockOwner(owner);
    const owners = { ...(this.bodyScrollLockOwners || {}) };
    if (!owners[normalizedOwner]) {
      this.syncBodyScrollLockAttributes();
      return;
    }

    delete owners[normalizedOwner];
    this.bodyScrollLockOwners = owners;
    this.syncBodyScrollLockAttributes();

    if (Object.keys(owners).length > 0) return;

    document.body.style.cssText = '';
    document.documentElement.style.overflow = '';

    const scrollPosition = Number(this.bodyScrollLockPosition);
    this.bodyScrollLockPosition = undefined;
    if (Number.isFinite(scrollPosition)) {
      window.scrollTo(0, scrollPosition);
    }
  },

  forceReleaseAllBodyScrollLocks() {
    this.bodyScrollLockOwners = {};
    this.bodyScrollLockPosition = undefined;

    if (typeof document !== 'undefined') {
      if (document.body) {
        document.body.style.cssText = '';
      }

      if (document.documentElement) {
        document.documentElement.style.overflow = '';
      }
    }

    this.syncBodyScrollLockAttributes();
  },

  lockScroll() {
    this.acquireBodyScrollLock('modal');
  },

  unlockScroll() {
    this.releaseBodyScrollLock('modal');
  },

  // Core Modal
  openModal(type, extra = {}) {
    const activeElement = document.activeElement;
    const isHTMLElement = typeof HTMLElement !== 'undefined' && activeElement instanceof HTMLElement;
    this.modalTriggerEl = isHTMLElement ? activeElement : null;

    this.modal = { open: true, type, ...extra };
    this.lockScroll();
    this.focusModalDialog();
  },

  closeModal(options = {}) {
    const { restoreFocus = true } = options;
    const modalType = this.modal.type;
    if (modalType === 'notify') {
      this.resetNotifyState();
    }
    if (modalType === 'customize') {
      if (!this.customizeApplied) {
        this.restoreCustomizeSessionState();
      }
      this.resetCustomizeSessionState();
    }
    if (
      modalType === 'accessory-customize'
      && typeof this.isKeyboardAccessoryCustomizeModal === 'function'
      && this.isKeyboardAccessoryCustomizeModal()
      && !this.customizeApplied
    ) {
      this.restoreCustomizeSessionState();
      this.resetCustomizeSessionState();
    }
    if (modalType === 'accessory-customize' && typeof this.resetAccessoryCustomizeState === 'function') {
      this.resetAccessoryCustomizeState();
    }
    this.modal = { open: false, type: null };
    this.unlockScroll();
    if (restoreFocus) this.restoreModalFocus();
    else this.modalTriggerEl = null;
  },

  dismissTransientUiState() {
    if (typeof this._clearPendingPreviewDrawerHandoff === 'function') {
      this._clearPendingPreviewDrawerHandoff();
    }

    if (this.modal?.open) {
      this.closeModal({ restoreFocus: false });
    } else {
      this.modalTriggerEl = null;
    }

    if (this.sideCartDrawerOpen && typeof this.closeSideCartDrawer === 'function') {
      this.closeSideCartDrawer();
    } else {
      this.sideCartDrawerOpen = false;
    }

    if (typeof this._resetMobileBarDragState === 'function') {
      this._resetMobileBarDragState();
    }

    if (typeof this._clearSideCartFocusEffects === 'function') {
      this._clearSideCartFocusEffects();
    }

    if (Object.keys(this.bodyScrollLockOwners || {}).length > 0) {
      this.forceReleaseAllBodyScrollLocks();
    } else {
      this.syncBodyScrollLockAttributes();
    }
  },

  getModalAriaLabel() {
    if (this.modal.type === 'tablet') {
      return this.tabletModal?.title || 'Select your tablet';
    }
    if (this.modal.type === 'accessory') {
      return this.currentAccessoryButton?.label || 'Select accessory';
    }
    if (this.modal.type === 'customize') {
      return this.getCustomizeModalTitle();
    }
    if (this.modal.type === 'accessory-customize') {
      if (typeof this.getAccessoryCustomizeModalTitle === 'function') {
        return this.getAccessoryCustomizeModalTitle();
      }
      const label = this.currentAccessoryButton?.label || 'Accessory';
      return `Customize ${label}`;
    }
    if (this.modal.type === 'vehicle-help') {
      return "Can't find your vehicle?";
    }
    if (this.modal.type === 'notify') {
      return 'Get notified';
    }
    return 'Dialog';
  },

  getModalDialogElement() {
    return this.$refs?.modalDialog || null;
  },

  getModalFocusableElements() {
    const dialog = this.getModalDialogElement();
    if (!dialog) return [];

    const selector = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'iframe:not([tabindex="-1"])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(',');

    return Array.from(dialog.querySelectorAll(selector)).filter(el => {
      if (!(el instanceof HTMLElement)) return false;
      if (!el.isConnected || el.getAttribute('aria-hidden') === 'true' || el.tabIndex < 0) return false;

      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  },

  focusModalDialog() {
    if (!this.modal.open) return;

    const setFocus = () => {
      const dialog = this.getModalDialogElement();
      if (!dialog) return;

      const focusables = this.getModalFocusableElements();
      const target = focusables[0] || dialog;
      target.focus();
    };

    this.$nextTick(() => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(setFocus);
      } else {
        setTimeout(setFocus, 0);
      }
    });
  },

  trapModalFocus(event) {
    if (!this.modal.open || event.key !== 'Tab') return;

    const dialog = this.getModalDialogElement();
    if (!dialog) return;

    const focusables = this.getModalFocusableElements();
    if (focusables.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (!dialog.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  },

  restoreModalFocus() {
    const trigger = this.modalTriggerEl;
    this.modalTriggerEl = null;

    if (!trigger || !trigger.isConnected || typeof trigger.focus !== 'function') return;

    this.$nextTick(() => {
      trigger.focus();
    });
  },

  getTabletSelectionSignature() {
    const make = this.context.tablet_make || '';
    const model = this.context.tablet_model || '';
    const caseType = this.context.tablet_case_type || '';
    return `${make}||${model}||${caseType}`;
  },

  getChangedTabletSelectionKeys(previousSelection = {}, nextSelection = {}) {
    const changedKeys = [];
    const trackedKeys = ['tablet_make', 'tablet_model', 'tablet_case_type'];

    trackedKeys.forEach(key => {
      const previousValue = previousSelection?.[key] || null;
      const nextValue = nextSelection?.[key] || null;
      if (previousValue !== nextValue) {
        changedKeys.push(key);
      }
    });

    if (changedKeys.length > 0) {
      changedKeys.push('tablet_selection');
    }

    return changedKeys;
  },

  resetTabletCustomizationState() {
    delete this.context.tablet_mount;
    delete this.context.tablet_case;
    delete this.context.tablet_dock;
    delete this.context.tablet_keyboard;
    this.customization = null;
    this.customizeSelections = {};
    this.customizeError = null;
    this.customizeTarget = null;
  },

  clearTabletKeyboardContext() {
    if (!this.context || typeof this.context !== 'object') return;

    delete this.context.tablet_keyboard;

    if (this.context.tablet && typeof this.context.tablet === 'object' && !Array.isArray(this.context.tablet)) {
      const tablet = { ...this.context.tablet };
      delete tablet.keyboard;
      this.context.tablet = tablet;
    }
  },

  snapshotCustomizeSessionState() {
    this.customizeApplied = false;
    this.customizeTabletSignature = this.getTabletSelectionSignature();
    this.customizeTabletSelectionBackup = {
      tablet_make: this.context.tablet_make || null,
      tablet_model: this.context.tablet_model || null,
      tablet_case_type: this.context.tablet_case_type || null
    };
    this.customizeContextBackup = {
      tablet_mount: this.context.tablet_mount,
      tablet_case: this.context.tablet_case,
      tablet_dock: this.context.tablet_dock,
      tablet_keyboard: this.context.tablet_keyboard
    };
  },

  restoreCustomizeSessionState() {
    const selection = this.customizeTabletSelectionBackup || {};
    if (selection.tablet_make) this.context.tablet_make = selection.tablet_make;
    else delete this.context.tablet_make;
    if (selection.tablet_model) this.context.tablet_model = selection.tablet_model;
    else delete this.context.tablet_model;
    if (selection.tablet_case_type) this.context.tablet_case_type = selection.tablet_case_type;
    else delete this.context.tablet_case_type;

    const contextBackup = this.customizeContextBackup || {};
    const contextKeys = ['tablet_mount', 'tablet_case', 'tablet_dock', 'tablet_keyboard'];
    contextKeys.forEach(key => {
      if (contextBackup[key] === undefined) {
        delete this.context[key];
        return;
      }
      this.context[key] = contextBackup[key];
    });
  },

  resetCustomizeSessionState() {
    this.customizeApplied = false;
    this.customizeTabletSignature = null;
    this.customizeTabletSelectionBackup = null;
    this.customizeContextBackup = null;
  },

  // Tablet Modal
  async openTabletModal() {
    await this.openCustomizeModal('tablet');
  },

  async openLaptopModal() {
    await this.openCustomizeModal('laptop');
  },

  async openDeviceCustomizeModal(deviceId) {
    if (deviceId === 'tablet') {
      await this.openCustomizeModal('tablet');
      return;
    }

    if (deviceId === 'laptop') {
      await this.openCustomizeModal('laptop');
    }
  },

  cancelTabletSelection() {
    const backup = this.tabletSelectionBackup || {};
    if (backup.tablet_make) this.context.tablet_make = backup.tablet_make;
    else delete this.context.tablet_make;
    if (backup.tablet_model) this.context.tablet_model = backup.tablet_model;
    else delete this.context.tablet_model;
    if (backup.tablet_case_type) this.context.tablet_case_type = backup.tablet_case_type;
    else delete this.context.tablet_case_type;

    this.tabletSelectionBackup = null;
    this.closeModal();
  },

  selectTabletMake(make) {
    this.context.tablet_make = make;
    // Only cascade (clear dependent fields) during user selection, not restoration
    if (!this.restoringTabletSelection) {
      this.context.tablet_model = '';
      this.context.tablet_case_type = '';
    }
  },

  selectTabletModel(model) {
    this.context.tablet_model = model;
    // Only cascade during user selection, not restoration
    if (!this.restoringTabletSelection) {
      this.context.tablet_case_type = '';
    }
  },

  async confirmTabletSelection() {
    if (!this.canConfirmTablet) return;
    if (this.tabletModal?.options && Object.keys(this.tabletModal.options).length > 0) {
      this.tabletOptionsCache = this.tabletModal.options;
    }
    const nextSignature = this.getTabletSelectionSignature();
    const selectionChanged = this.tabletSelectionSignature !== nextSignature;
    const nextConfirmedSelection = {
      tablet_make: this.context.tablet_make || null,
      tablet_model: this.context.tablet_model || null,
      tablet_case_type: this.context.tablet_case_type || null
    };
    const previousConfirmedSelection = this.tabletSelectionConfirmed || this.tabletSelectionBackup || {};
    const changedTabletKeys = this.getChangedTabletSelectionKeys(previousConfirmedSelection, nextConfirmedSelection);
    const previousContext = {
      laptop_selected: this.context.laptop_selected,
      other_device_selected: this.context.other_device_selected,
      tablet_mount: this.context.tablet_mount,
      tablet_case: this.context.tablet_case,
      tablet_dock: this.context.tablet_dock,
      tablet_keyboard: this.context.tablet_keyboard
    };
    const previousCustomizeState = {
      customization: this.customization,
      customizeSelections: this.customizeSelections,
      customizeError: this.customizeError,
      customizeTarget: this.customizeTarget
    };
    let prunedAccessories = [];
    const restoreContextValue = (key, value) => {
      if (value === undefined) {
        delete this.context[key];
        return;
      }
      this.context[key] = value;
    };

    if (selectionChanged) {
      this.resetTabletCustomizationState();
      if (typeof this.pruneAccessoriesForContextChange === 'function') {
        prunedAccessories = this.pruneAccessoriesForContextChange(changedTabletKeys);
      }
    }

    // Clear other device selections when confirming tablet
    delete this.context.laptop_selected;
    delete this.context.other_device_selected;

    // Call API to get computed tablet kit (preloads)
    // Send current_step: 2 to tell backend we're still on step 2
    await this.api({ current_step: 2 });
    if (this.error) {
      restoreContextValue('laptop_selected', previousContext.laptop_selected);
      restoreContextValue('other_device_selected', previousContext.other_device_selected);

      if (selectionChanged) {
        restoreContextValue('tablet_mount', previousContext.tablet_mount);
        restoreContextValue('tablet_case', previousContext.tablet_case);
        restoreContextValue('tablet_dock', previousContext.tablet_dock);
        restoreContextValue('tablet_keyboard', previousContext.tablet_keyboard);

        this.customization = previousCustomizeState.customization;
        this.customizeSelections = previousCustomizeState.customizeSelections;
        this.customizeError = previousCustomizeState.customizeError;
        this.customizeTarget = previousCustomizeState.customizeTarget;
      }
      if (prunedAccessories.length > 0 && typeof this.restorePrunedAccessories === 'function') {
        this.restorePrunedAccessories(prunedAccessories);
      }

      return;
    }

    this.tabletSelectionSignature = nextSignature;
    this.tabletSelectionConfirmed = nextConfirmedSelection;
    this.tabletSelectionBackup = null;

    // Store tablet kit from response, clear other device kits
    this.localKit = {
      tablet: this.response.kit?.tablet || { products: [], images: [] },
      laptop: { products: [], images: [] },
      other_device: { products: [], images: [] }
    };

    if (this.step >= 2 && typeof this.syncSnapshotWithCurrentState === 'function') {
      try {
        await this.syncSnapshotWithCurrentState(true);
      } catch (snapshotError) {
        console.warn('[Tough-Bar Tablet] Snapshot refresh failed:', snapshotError);
      }
    }

    // Fetch product data for the updated tablet kit
    const tabletKit = this.localKit.tablet;
    if (tabletKit?.products?.length > 0) {
      const fetchPromises = tabletKit.products.map(handle => this.fetchProductData(handle));
      await Promise.all(fetchPromises);
    }

    this.closeModal();
  },

  clearCustomizeDataState() {
    this.customization = null;
    this.customizeSelections = {};
    this.customizePrices = { subtotal: null };
  },

  isCompleteTabletSelection(selection = {}) {
    return Boolean(
      selection.tablet_make &&
      selection.tablet_model &&
      selection.tablet_case_type
    );
  },

  getPreferredCustomizeTabletSelection() {
    const contextSelection = {
      tablet_make: this.context.tablet_make || null,
      tablet_model: this.context.tablet_model || null,
      tablet_case_type: this.context.tablet_case_type || null
    };
    if (this.isCompleteTabletSelection(contextSelection)) {
      return contextSelection;
    }

    const confirmedSelection = this.tabletSelectionConfirmed || {};
    if (this.isCompleteTabletSelection(confirmedSelection)) {
      return {
        tablet_make: confirmedSelection.tablet_make,
        tablet_model: confirmedSelection.tablet_model,
        tablet_case_type: confirmedSelection.tablet_case_type
      };
    }

    return contextSelection;
  },

  applyTabletSelection(selection = {}) {
    this.restoringTabletSelection = true;
    try {
      const make = selection.tablet_make;
      const model = selection.tablet_model;
      const caseType = selection.tablet_case_type;

      if (make) this.context.tablet_make = make;
      else delete this.context.tablet_make;

      if (model) this.context.tablet_model = model;
      else delete this.context.tablet_model;

      if (caseType) this.context.tablet_case_type = caseType;
      else delete this.context.tablet_case_type;
    } finally {
      this.restoringTabletSelection = false;
    }
  },

  captureCustomizeContextState() {
    const keys = ['tablet_mount', 'tablet_case', 'tablet_dock', 'tablet_keyboard'];
    const snapshot = {};
    if (!this.context) return snapshot;
    keys.forEach(key => {
      if (Object.prototype.hasOwnProperty.call(this.context, key)) {
        snapshot[key] = this.context[key];
      }
    });
    return snapshot;
  },

  restoreCustomizeContextState(state = {}) {
    const keys = ['tablet_mount', 'tablet_case', 'tablet_dock', 'tablet_keyboard'];
    if (!this.context) return;
    keys.forEach(key => {
      if (Object.prototype.hasOwnProperty.call(state, key)) {
        this.context[key] = state[key];
      } else {
        delete this.context[key];
      }
    });
  },

  applyPendingCustomizeContextRestore() {
    if (!this.pendingCustomizeContextRestore) return;
    this.restoreCustomizeContextState(this.pendingCustomizeContextRestore);
    this.pendingCustomizeContextRestore = null;
  },

  isTabletSelectionStagedInOpenModal() {
    if (!this.modal?.open) return false;
    return this.modal.type === 'tablet'
      || (this.modal.type === 'customize' && this.getCurrentCustomizeTarget() === 'tablet');
  },

  getTabletSelectionDisplayBackup() {
    if (!this.modal?.open) return {};
    if (this.modal.type === 'customize' && this.getCurrentCustomizeTarget() === 'tablet') {
      return this.customizeTabletSelectionBackup || {};
    }
    if (this.modal.type === 'tablet') {
      return this.tabletSelectionBackup || {};
    }
    return {};
  },

  hasCommittedTabletSelection() {
    return this.isCompleteTabletSelection(this.tabletSelectionConfirmed || {})
      || Boolean(this.kit?.tablet?.products?.length > 0);
  },

  hasCustomizeTabletSelection() {
    return Boolean(this.context.tablet_make && this.context.tablet_model && this.context.tablet_case_type);
  },

  hasValidCustomizeTabletSelection() {
    if (!this.hasCustomizeTabletSelection()) return false;
    return this.tabletCaseTypes.length === 0 || this.tabletCaseTypes.includes(this.context.tablet_case_type);
  },

  getCurrentCustomizeTarget() {
    const normalized = String(this.customizeTarget || 'tablet').trim().toLowerCase();
    return normalized === 'laptop' ? 'laptop' : 'tablet';
  },

  hasCommittedLaptopSelection() {
    return Boolean(this.context.laptop_selected || this.kit?.laptop?.products?.length > 0);
  },

  hasValidCustomizeLaptopSelection() {
    const field = this.getLaptopStyleField();
    if (!field) return false;

    const options = this.getLaptopStyleOptions();
    const value = this.getCustomizeSelectionValue('laptop', 'style');
    return options.includes(value);
  },

  getCustomizeModalTitle() {
    if (this.customization?.title) return this.customization.title;

    if (this.getCurrentCustomizeTarget() === 'laptop') {
      return 'Customize Laptop Kit';
    }

    return this.hasCommittedTabletSelection()
      ? 'Customize Your Kit'
      : (this.tabletModal?.title || 'Select Your Tablet');
  },

  getCustomizeHeaderSubtitle() {
    if (this.getCurrentCustomizeTarget() === 'laptop') {
      return 'Choose the laptop tray style that fits how you work.';
    }

    return (this.customization || this.hasCommittedTabletSelection())
      ? 'Adjust your tablet kit options to fit how you work.'
      : 'Choose your make, model, and case type to load the right kit.';
  },

  canApplyCustomizeModal() {
    if (this.getCurrentCustomizeTarget() === 'laptop') {
      return this.hasValidCustomizeLaptopSelection();
    }
    return this.hasValidCustomizeTabletSelection();
  },

  getCustomizeEmptyStateMessage() {
    if (this.getCurrentCustomizeTarget() === 'laptop') {
      return 'No laptop style options are available right now.';
    }

    if (this.hasValidCustomizeTabletSelection()) {
      return 'No additional customization options are available for this tablet. Apply to continue.';
    }
    return 'Select your tablet to load available options.';
  },

  getCustomizeApplyButtonLabel() {
    if (this.customization) return 'Apply';
    if (this.getCurrentCustomizeTarget() === 'laptop') {
      return this.hasCommittedLaptopSelection() ? 'Save' : 'Select Laptop';
    }
    return this.hasCommittedTabletSelection() ? 'Save' : 'Select Tablet';
  },

  getTabletOptionsFromPayload(payload = null) {
    const buttons = payload?.section?.buttons;
    if (!Array.isArray(buttons)) return null;

    const tabletButton = buttons.find(button => button?.id === 'tablet');
    const options = tabletButton?.modal?.options;
    if (!options || typeof options !== 'object') return null;
    if (Object.keys(options).length === 0) return null;

    return options;
  },

  isSingleTabletSelectionOptions(options = null) {
    if (!options || typeof options !== 'object') return false;

    const makes = Object.keys(options);
    if (makes.length !== 1) return false;

    const onlyMake = makes[0];
    const modelsMap = options[onlyMake];
    if (!modelsMap || typeof modelsMap !== 'object') return false;

    const models = Object.keys(modelsMap);
    if (models.length !== 1) return false;

    const onlyModel = models[0];
    const caseTypes = modelsMap[onlyModel];
    return Array.isArray(caseTypes) && caseTypes.length === 1;
  },

  async ensureTabletOptionsCache({ force = false } = {}) {
    const modalOptions = this.tabletModal?.options;
    if (modalOptions && Object.keys(modalOptions).length > 0) {
      this.tabletOptionsCache = modalOptions;
      return true;
    }

    const cache = this.tabletOptionsCache || {};
    const hasCache = Object.keys(cache).length > 0;
    const sharedSession = Boolean(this.currentSharedConfigId || this.sharedConfigIdFromUrl);
    const shouldFetch = force || !hasCache || (sharedSession && this.isSingleTabletSelectionOptions(cache));
    if (!shouldFetch) return true;

    try {
      const payload = this.buildApiContext({ current_step: 2 });
      const data = await this.postToBuilder(this.getBuilderEndpoint(), payload);
      const options = this.getTabletOptionsFromPayload(data);
      if (options) {
        this.tabletOptionsCache = options;
        return true;
      }
    } catch (error) {
      console.warn('[Tough-Bar Tablet] Failed to hydrate tablet options cache:', error);
    }

    return Object.keys(this.tabletOptionsCache || {}).length > 0;
  },

  async requestCustomizeDataForCurrentTablet(requestOptions = {}) {
    const allowedModalTypes = Array.isArray(requestOptions.allowedModalTypes) && requestOptions.allowedModalTypes.length > 0
      ? requestOptions.allowedModalTypes
      : ['customize'];
    const requestedSelection = {
      tablet_make: this.context.tablet_make || null,
      tablet_model: this.context.tablet_model || null,
      tablet_case_type: this.context.tablet_case_type || null
    };
    const requestedSignature = this.getTabletSelectionSignature();
    const body = this.buildApiContext({ customize: 'tablet' });
    const data = await this.postToBuilder(this.getBuilderEndpoint(), body);
    if (!allowedModalTypes.includes(this.modal.type) || requestedSignature !== this.getTabletSelectionSignature()) {
      this.applyPendingCustomizeContextRestore();
      return;
    }

    const options = this.getTabletOptionsFromPayload(data);
    if (options) {
      this.tabletOptionsCache = options;
    }

    this.applyTabletSelection(requestedSelection);
    this.customization = data.customization || null;
    this.customizeTarget = data.customize || 'tablet';
    this.customizeTabletSignature = requestedSignature;
    this.applyPendingCustomizeContextRestore();
    this.initCustomizeSelections();
    await this.fetchCustomizeProducts();
  },

  async requestCustomizeDataForCurrentLaptop() {
    const data = await this.postToBuilder(this.getBuilderEndpoint(), this.buildApiContext({ customize: 'laptop' }));
    if (this.modal.type !== 'customize' || this.getCurrentCustomizeTarget() !== 'laptop') {
      return;
    }

    this.customization = data.customization || null;
    this.customizeTarget = data.customize || 'laptop';
    this.initCustomizeSelections();
    await this.fetchCustomizeProducts();
  },

  async refreshCustomizeForTabletSelection({ force = false, allowedModalTypes = ['customize'] } = {}) {
    if (this.customizeLoading) return;
    this.customizeError = null;

    if (!this.hasCustomizeTabletSelection()) {
      this.clearCustomizeDataState();
      return;
    }

    if (!this.hasValidCustomizeTabletSelection()) {
      this.clearCustomizeDataState();
      this.customizeError = 'Select a valid tablet case type before customizing.';
      return;
    }

    const currentSignature = this.getTabletSelectionSignature();
    if (!force && this.customization && this.customizeTabletSignature === currentSignature) {
      return;
    }

    const previousSignature = typeof this.customizeTabletSignature === 'string'
      ? this.customizeTabletSignature
      : null;
    const shouldResetTabletCustomizationContext = previousSignature !== null && previousSignature !== currentSignature;

    if (shouldResetTabletCustomizationContext) {
      this.pendingCustomizeContextRestore = this.captureCustomizeContextState();
      delete this.context.tablet_mount;
      delete this.context.tablet_case;
      delete this.context.tablet_dock;
      delete this.context.tablet_keyboard;
    } else {
      this.pendingCustomizeContextRestore = null;
    }

    this.clearCustomizeDataState();
    this.customizeLoading = true;

    try {
      await this.requestCustomizeDataForCurrentTablet({ allowedModalTypes });
    } catch (err) {
      console.error('[Tough-Bar Customize] Failed to load customization:', err);
      this.customizeError = 'Failed to load customization options. Please try again.';
      if (this.pendingCustomizeContextRestore) {
        this.restoreCustomizeContextState(this.pendingCustomizeContextRestore);
        this.pendingCustomizeContextRestore = null;
      }
    } finally {
      this.customizeLoading = false;
    }
  },

  async updateCustomizeTabletMake(make) {
    if (this.restoringTabletSelection) return;
    this.selectTabletMake(make);
    await this.refreshCustomizeForTabletSelection();
  },

  async updateCustomizeTabletModel(model) {
    if (this.restoringTabletSelection) return;
    this.selectTabletModel(model);
    await this.refreshCustomizeForTabletSelection();
  },

  async updateCustomizeTabletCaseType(caseType) {
    if (this.restoringTabletSelection) return;
    this.context.tablet_case_type = caseType;
    await this.refreshCustomizeForTabletSelection();
  },

  async refreshCustomizeForLaptopSelection({ force = false } = {}) {
    if (this.customizeLoading) return;
    if (!force && this.customization && this.getCurrentCustomizeTarget() === 'laptop') {
      return;
    }

    this.customizeError = null;
    this.clearCustomizeDataState();
    this.customizeLoading = true;

    try {
      await this.requestCustomizeDataForCurrentLaptop();
    } catch (err) {
      console.error('[Tough-Bar Customize] Failed to load laptop customization:', err);
      this.customizeError = 'Failed to load laptop customization options. Please try again.';
    } finally {
      this.customizeLoading = false;
    }
  },

  async openCustomizeModal(target = 'tablet') {
    const normalizedTarget = String(target || 'tablet').trim().toLowerCase() === 'laptop' ? 'laptop' : 'tablet';

    this.customizeTarget = normalizedTarget;
    this.customizeError = null;
    this.clearCustomizeDataState();

    if (normalizedTarget === 'laptop') {
      this.snapshotCustomizeSessionState();
      this.openModal('customize');
      await this.refreshCustomizeForLaptopSelection({ force: true });
      return;
    }

    const preferredSelection = this.getPreferredCustomizeTabletSelection();
    this.applyTabletSelection(preferredSelection);
    this.snapshotCustomizeSessionState();

    const shouldForceTabletOptionsHydration = Boolean(this.currentSharedConfigId || this.sharedConfigIdFromUrl);
    const tabletOptionsPromise = this.ensureTabletOptionsCache({ force: shouldForceTabletOptionsHydration });

    this.openModal('customize');
    await Promise.all([
      tabletOptionsPromise,
      this.refreshCustomizeForTabletSelection({ force: true })
    ]);
  },

  initCustomizeSelections() {
    if (!this.customization?.sections) {
      this.customizeSelections = {};
      return;
    }

    const selections = {};
    this.customization.sections.forEach(section => {
      let saved = {};
      if (this.getCurrentCustomizeTarget() === 'laptop' && section.id === 'laptop') {
        saved = this.context.laptop_style ? { style: this.context.laptop_style } : {};
      } else {
        const contextKey = `tablet_${section.id}`;
        saved = this.context[contextKey] || {};
      }
      const current = section.current || {};
      const preload = section.preload || {};
      const sectionSelections = {};

      section.fields?.forEach(field => {
        let value = saved[field.key];
        if (value === undefined) value = current[field.key];
        if (value === undefined) value = preload[field.key];
        if (Array.isArray(value)) value = value[0];
        if (value === undefined) value = null;
        sectionSelections[field.key] = value;
      });

      selections[section.id] = sectionSelections;
    });

    this.customizeSelections = selections;
    this.syncAllCustomizeSelections();
    this.updateCustomizePrices();
  },

  async resetCustomizeSelections() {
    this.customizeError = null;

    if (!this.customization?.sections) {
      if (this.getCurrentCustomizeTarget() === 'tablet') {
        // If tablet selection changes to a state with no customization data,
        // reset should still recover the original in-session tablet selection.
        this.restoreCustomizeSessionState();
        await this.refreshCustomizeForTabletSelection({ force: true });
      } else {
        await this.refreshCustomizeForLaptopSelection({ force: true });
      }
      return;
    }

    const selections = {};
    this.customization.sections.forEach(section => {
      const preload = section.preload || {};
      const sectionSelections = {};

      section.fields?.forEach(field => {
        let value = preload[field.key];
        if (Array.isArray(value)) value = value[0];
        if (value === undefined) value = null;
        sectionSelections[field.key] = value;
      });

      selections[section.id] = sectionSelections;
    });

    this.customizeSelections = selections;
    this.syncAllCustomizeSelections();
    this.updateCustomizePrices();
    await this.fetchCustomizeProducts();
  },

  syncAllCustomizeSelections() {
    if (!this.customization?.sections) return;
    for (let i = 0; i < 3; i++) {
      this.customization.sections.forEach(section => {
        this.syncCustomizeSelections(section.id);
      });
    }
  },

  syncCustomizeSelections(sectionId) {
    const section = this.getCustomizeSection(sectionId);
    if (!section) return;

    const current = { ...(this.customizeSelections[sectionId] || {}) };
    const preload = section.preload || {};
    const getDraftSelections = () => ({
      ...(this.customizeSelections || {}),
      [sectionId]: current
    });

    this.getCustomizeSyncFields(sectionId, section.fields).forEach(field => {
      if (field.type === 'toggle') {
        const options = this.getCustomizeToggleOptions(sectionId, field.key, getDraftSelections());
        if (options.length === 0) {
          current[field.key] = null;
          return;
        }

        const hasBooleanValue = current[field.key] === true || current[field.key] === false;
        if (!hasBooleanValue || !options.includes(current[field.key])) {
          const scopedPreloadValue = this.getCustomizeScopedPreloadValue(sectionId, field.key, getDraftSelections());
          if ((scopedPreloadValue === true || scopedPreloadValue === false) && options.includes(scopedPreloadValue)) {
            current[field.key] = scopedPreloadValue;
            return;
          }

          const preloadValue = preload[field.key];
          if ((preloadValue === true || preloadValue === false) && options.includes(preloadValue)) {
            current[field.key] = preloadValue;
            return;
          }

          // Prefer false for optional toggles (for example LED light) when available.
          if (options.includes(false)) {
            current[field.key] = false;
            return;
          }

          current[field.key] = options[0];
        }
        return;
      }

      const options = this.getCustomizeFieldOptions(sectionId, field, getDraftSelections());

      if (options.length === 0) {
        current[field.key] = null;
        return;
      }

      if (current[field.key] === null || current[field.key] === undefined || !options.includes(current[field.key])) {
        if (sectionId === 'case' && field.key === 'ergo_accessory') {
          const noneValue = this.getErgoAccessoryNoneValue(options);
          if (noneValue) {
            current[field.key] = noneValue;
            return;
          }
        }

        const scopedPreloadValue = this.getCustomizeScopedPreloadValue(sectionId, field.key, getDraftSelections());
        if (scopedPreloadValue !== undefined && scopedPreloadValue !== null && options.includes(scopedPreloadValue)) {
          current[field.key] = scopedPreloadValue;
          return;
        }

        const preloadValue = preload[field.key];
        if (preloadValue !== undefined && options.includes(preloadValue)) {
          current[field.key] = preloadValue;
          return;
        }
        if (field.key === 'keyboard_type') {
          const noneValue = this.getKeyboardNoneValue(options);
          current[field.key] = noneValue || options[0];
          return;
        }
        current[field.key] = options[0];
      }
    });

    this.customizeSelections = { ...this.customizeSelections, [sectionId]: current };
  },

  getCustomizeScopedPreloadValue(sectionId, fieldKey, selectionSource = null) {
    const entries = this.getCustomizeEntries(sectionId, fieldKey, selectionSource);
    const preloadEntry = entries.find(entry => entry?.preload === true);
    if (!preloadEntry) return undefined;

    const value = preloadEntry[fieldKey];
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : undefined;
    }

    return value;
  },

  getCustomizeSection(sectionId) {
    return this.customization?.sections?.find(section => section.id === sectionId) || null;
  },

  getCustomizeField(sectionId, fieldKey) {
    return this.getCustomizeSection(sectionId)?.fields?.find(field => field.key === fieldKey) || null;
  },

  getLaptopStyleField() {
    return this.getCustomizeField('laptop', 'style');
  },

  getLaptopStyleOptions() {
    const field = this.getLaptopStyleField();
    if (!field) return [];
    return this.getCustomizeFieldOptions('laptop', field);
  },

  getLaptopStyleCardImage(option) {
    const customizeModalImage = this.getCustomizeOptionImage('style', option);
    if (customizeModalImage) return customizeModalImage;

    const entry = this.getCustomizeSection('laptop')?.entries?.find(item => item?.style === option);
    return Array.isArray(entry?.images) && entry.images.length > 0 ? entry.images[0] : null;
  },

  isLaptopStyleSelected(option) {
    return this.getCustomizeSelectionValue('laptop', 'style') === option;
  },

  selectLaptopStyle(option) {
    this.updateCustomizeSelection('laptop', 'style', option, 'select');
  },

  getCustomizeDisplayFields(sectionId) {
    const section = this.getCustomizeSection(sectionId);
    if (!section) return [];

    if (sectionId !== 'mount') {
      return (section.fields || []).map(field => ({ sectionId, field }));
    }

    const keyboardFields = this.shouldShowCustomizeSection('keyboard')
      ? (this.getCustomizeSection('keyboard')?.fields || [])
      : [];
    const displayFields = [];

    (section.fields || []).forEach(field => {
      displayFields.push({ sectionId, field });
      if (field.key === 'configuration' && keyboardFields.length > 0) {
        keyboardFields.forEach(keyboardField => {
          displayFields.push({ sectionId: 'keyboard', field: keyboardField });
        });
      }
    });

    return displayFields;
  },

  getCustomizeConfigField() {
    const section = this.getCustomizeSection('mount');
    const field = section?.fields?.find(item => item.key === 'configuration') || null;
    return field ? { sectionId: 'mount', field } : null;
  },

  getCustomizeNonConfigFields(sectionId) {
    return this.getCustomizeDisplayFields(sectionId).filter(
      item => !(item.sectionId === 'mount' && item.field.key === 'configuration')
    );
  },

  getCustomizeFieldTypes() {
    const types = {};
    (this.customization?.sections || []).forEach(section => {
      (section.fields || []).forEach(field => {
        if (!types[field.key]) {
          types[field.key] = field.type || 'select';
        }
      });
    });
    return types;
  },

  getCustomizeOrderedFieldKeys(sectionId) {
    if (sectionId === 'case') {
      return ['connection_type', 'series', 'led_light', 'ergo_accessory', 'color'];
    }
    if (sectionId === 'dock') {
      return ['holder_style', 'lock_type', 'dock_style', 'power_type', 'peripheral_support'];
    }
    if (sectionId === 'mount') {
      return ['configuration', 'adjustability', 'locking'];
    }
    return [];
  },

  getCustomizeSyncFields(sectionId, fields = []) {
    const sectionFields = Array.isArray(fields) ? fields : [];
    const fieldOrder = this.getCustomizeOrderedFieldKeys(sectionId);
    if (fieldOrder.length === 0) return sectionFields;

    return [...sectionFields].sort((a, b) => {
      const aIndex = fieldOrder.indexOf(a.key);
      const bIndex = fieldOrder.indexOf(b.key);
      const normalizedAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
      const normalizedBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
      if (normalizedAIndex !== normalizedBIndex) return normalizedAIndex - normalizedBIndex;
      return sectionFields.indexOf(a) - sectionFields.indexOf(b);
    });
  },

  getCustomizeConstraints(sectionId, selectionSource = null) {
    const constraints = {};
    const propagation = this.customization?.propagation || {};
    const selections = selectionSource || this.customizeSelections || {};
    Object.entries(propagation).forEach(([fieldKey, rule]) => {
      if (!rule?.affects?.includes(sectionId)) return;
      const sourceSelections = selections?.[rule.source] || {};
      const value = sourceSelections[fieldKey];
      if (value !== undefined && value !== null && value !== '') {
        constraints[fieldKey] = value;
      }
    });
    return constraints;
  },

  shouldIgnoreCustomizeConstraint(sectionId, ignoreFieldKey, constraintKey) {
    return sectionId === 'mount'
      && ignoreFieldKey === 'configuration'
      && constraintKey === 'holder_style';
  },

  customizeEntryMatches(entry, selections) {
    const fieldTypes = this.getCustomizeFieldTypes();
    return Object.entries(selections).every(([key, selected]) => {
      if (selected === null || selected === undefined || selected === '') return true;

      const entryValue = entry[key];
      const fieldType = fieldTypes[key];

      if (fieldType === 'toggle') {
        if (selected === true) return entryValue === true;
        if (selected === false) return entryValue === false;
        return true;
      }

      if (Array.isArray(entryValue)) return entryValue.includes(selected);
      if (entryValue === undefined || entryValue === null) return false;
      return entryValue === selected;
    });
  },

  getCustomizeEntries(sectionId, ignoreFieldKey = null, selectionSource = null) {
    const section = this.getCustomizeSection(sectionId);
    if (!section) return [];

    const sourceSelections = selectionSource || this.customizeSelections || {};
    const selections = { ...(sourceSelections[sectionId] || {}) };
    if (ignoreFieldKey) {
      delete selections[ignoreFieldKey];
    }

    const constraints = this.getCustomizeConstraints(sectionId, sourceSelections);
    if (ignoreFieldKey) {
      Object.keys(constraints).forEach(key => {
        if (this.shouldIgnoreCustomizeConstraint(sectionId, ignoreFieldKey, key)) {
          delete constraints[key];
        }
      });
    }

    // For mount section, configuration is the driver field.
    // Ignore sibling mount selections so valid configurations are never hidden.
    if (sectionId === 'mount' && ignoreFieldKey === 'configuration') {
      Object.keys(selections).forEach(key => {
        delete selections[key];
      });
    }

    if (ignoreFieldKey) {
      const fieldOrder = this.getCustomizeOrderedFieldKeys(sectionId);
      const currentIndex = fieldOrder.indexOf(ignoreFieldKey);
      if (currentIndex !== -1) {
        const allowed = new Set(fieldOrder.slice(0, currentIndex));
        Object.keys(selections).forEach(key => {
          if (!allowed.has(key)) delete selections[key];
        });
      }
    }

    const activeSelections = { ...constraints, ...selections };

    return (section.entries || []).filter(entry => this.customizeEntryMatches(entry, activeSelections));
  },

  getCustomizeFieldOptions(sectionId, field, selectionSource = null) {
    if (!field || field.type === 'toggle') return [];

    const entries = this.getCustomizeEntries(sectionId, field.key, selectionSource);
    const seen = new Set();
    const values = [];

    entries.forEach(entry => {
      const value = entry[field.key];
      if (Array.isArray(value)) {
        value.forEach(option => {
          if (option === null || option === undefined || seen.has(option)) return;
          seen.add(option);
          values.push(option);
        });
        return;
      }

      if (value === null || value === undefined || seen.has(value)) return;
      seen.add(value);
      values.push(value);
    });

    const available = field.available || field.options;
    if (Array.isArray(available) && available.length > 0) {
      const ordered = available.filter(option => seen.has(option));
      values.forEach(option => {
        if (!ordered.includes(option)) ordered.push(option);
      });
      return ordered;
    }

    return values;
  },

  getCustomizeToggleOptions(sectionId, fieldKey, selectionSource = null) {
    const section = this.getCustomizeSection(sectionId);
    if (!section) return [];
    const entries = this.getCustomizeEntries(sectionId, fieldKey, selectionSource);
    const values = new Set();
    entries.forEach(entry => {
      if (entry[fieldKey] === true) values.add(true);
      if (entry[fieldKey] === false) values.add(false);
    });
    return Array.from(values);
  },

  hasCustomizeFieldOptions(sectionId, field) {
    if (!field) return false;
    if (sectionId === 'case' && field.key === 'ergo_accessory') {
      return this.getCustomizeFieldOptions(sectionId, field).length > 1;
    }

    const type = field.type || 'select';
    if (type === 'toggle') {
      return this.getCustomizeToggleOptions(sectionId, field.key).length > 1;
    }

    const options = this.getCustomizeFieldOptions(sectionId, field);
    if (sectionId === 'dock') {
      return options.length > 1;
    }
    return options.length > 0;
  },

  hasCustomizeSectionOptions(sectionId) {
    const section = this.getCustomizeSection(sectionId);
    if (!section) return false;
    return (section.fields || []).some(field => this.hasCustomizeFieldOptions(sectionId, field));
  },

  getCustomizeSelectionValue(sectionId, fieldKey) {
    const value = this.customizeSelections?.[sectionId]?.[fieldKey];
    if (value === true) return 'true';
    if (value === false) return 'false';
    if (value === null || value === undefined) return '';
    return value;
  },

  getKeyboardField() {
    return this.getCustomizeSection('keyboard')?.fields?.find(field => field.key === 'keyboard_type') || null;
  },

  getKeyboardSwingField() {
    return this.getCustomizeSection('keyboard')?.fields?.find(field => field.key === 'swing_arms') || null;
  },

  getCustomizeFieldNoneValue(sectionId, fieldKey, options = null) {
    const values = options || (() => {
      const field = this.getCustomizeField(sectionId, fieldKey);
      return field ? this.getCustomizeFieldOptions(sectionId, field) : [];
    })();

    return values.find(value => typeof value === 'string' && value.trim().toLowerCase() === 'none')
      || values.find(value => typeof value === 'string' && value.toLowerCase().includes('none'))
      || null;
  },

  getKeyboardNoneValue(options = null) {
    return this.getCustomizeFieldNoneValue('keyboard', 'keyboard_type', options);
  },

  getErgoAccessoryNoneValue(options = null) {
    return this.getCustomizeFieldNoneValue('case', 'ergo_accessory', options);
  },

  getKeyboardCardOptions() {
    const field = this.getKeyboardField();
    if (!field) return [];
    const options = this.getCustomizeFieldOptions('keyboard', field);
    const noneValue = this.getKeyboardNoneValue(options);
    return noneValue ? options.filter(option => option !== noneValue) : options;
  },

  isKeyboardOptionSelected(option) {
    return this.getCustomizeSelectionValue('keyboard', 'keyboard_type') === option;
  },

  selectKeyboardOption(option) {
    this.updateCustomizeSelection('keyboard', 'keyboard_type', option, 'select');
  },

  toggleKeyboardOption(option) {
    const noneValue = this.getKeyboardNoneValue();
    if (this.isKeyboardOptionSelected(option)) {
      this.updateCustomizeSelection('keyboard', 'keyboard_type', noneValue || null, 'select');
      return;
    }
    this.selectKeyboardOption(option);
  },

  // Locking field helpers (for mount section)
  getLockingField() {
    return this.getCustomizeSection('mount')?.fields?.find(field => field.key === 'locking') || null;
  },

  shouldShowLockingOptions() {
    // Only show locking options for Offset configurations
    const config = this.customizeSelections?.mount?.configuration || '';
    if (!config.includes('Offset')) return false;

    const field = this.getLockingField();
    if (!field) return false;

    const options = this.getCustomizeFieldOptions('mount', field);
    return options.length > 1;
  },

  getLockingOptions() {
    const field = this.getLockingField();
    if (!field) return [];
    return this.getCustomizeFieldOptions('mount', field);
  },

  getLockingLabel(option) {
    if (option === true || option === 'Yes') return 'Locking';
    if (option === false || option === 'No') return 'Non-Locking';
    return option;
  },

  isLockingOptionSelected(option) {
    const currentValue = this.customizeSelections?.mount?.locking;
    // Handle both boolean and string comparisons
    if (option === true || option === 'Yes') {
      return currentValue === true || currentValue === 'Yes';
    }
    if (option === false || option === 'No') {
      return currentValue === false || currentValue === 'No';
    }
    return currentValue === option;
  },

  selectLockingOption(option) {
    this.updateCustomizeSelection('mount', 'locking', option, 'select');
  },

  getCustomizeOptionLabel(fieldKey, option) {
    const normalized = typeof option === 'string'
      ? option.replace(/\\"/g, '"')
      : option;
    if (fieldKey === 'configuration') {
      return normalized.replace(' + ', ' with ');
    }
    if (fieldKey === 'keyboard_type') {
      if (normalized === 'Track Pad') return 'Keyboard with Trackpad';
      if (normalized === '10-Key Numeric Pad') return '10 Key Numeric Keyboard';
    }
    return normalized;
  },

  getCustomizeOptionImage(fieldKey, option) {
    const base = 'https://images.rammount.com/images/builders/tough-bar/Customize-Modal/';
    const supported = [
      'configuration',
      'adjustability',
      'holder_style',
      'lock_type',
      'locking',
      'dock_style',
      'power_type',
      'keyboard_type',
      'swing_arms',
      'mount_type',
      'holder_type',
      'base_type',
      'style',
      'connection_type',
      'series',
      'ergo_accessory',
      'led_light'
    ];
    if (supported.includes(fieldKey)) {
      // Handle locking field (mount section - boolean Yes/No)
      if (fieldKey === 'locking') {
        let filename = null;
        if (option === true || option === 'Yes') filename = 'Locking';
        if (option === false || option === 'No') filename = 'Non-Locking';
        if (filename) {
          return `${base}${encodeURI(filename)}.jpg`;
        }
      }
      // Handle lock_type field (dock section)
      if (fieldKey === 'lock_type') {
        let filename = null;
        if (option === true) filename = 'Locking (Dock)';
        if (option === false) filename = 'Non-Locking (Dock)';
        if (!filename && typeof option === 'string') {
          const normalized = option.toLowerCase();
          if (normalized.includes('non') && normalized.includes('lock')) {
            filename = 'Non-Locking (Dock)';
          } else if (normalized.includes('lock')) {
            filename = 'Locking (Dock)';
          }
        }
        if (filename) {
          return `${base}${encodeURI(filename)}.jpg`;
        }
      }
      const normalized = typeof option === 'string'
        ? option.replace(/\\"/g, '"')
        : option;
      const filename = typeof normalized === 'string'
        ? normalized.replace(/"/g, '')
        : normalized;
      return `${base}${encodeURI(filename)}.jpg`;
    }
    return null;
  },

  getCustomizeColorStyle(option) {
    if (!option) return '';
    const value = String(option).trim().toLowerCase();
    if (value.startsWith('#') || value.startsWith('rgb')) {
      return `background-color: ${option}`;
    }
    if (value.includes('black')) return 'background-color: #0A0C0E';
    if (value.includes('white')) return 'background-color: #ffffff';
    if (value.includes('gray') || value.includes('grey')) return 'background-color: #d1d5db';
    if (value.includes('blue')) return 'background-color: #3D5A80';
    if (value.includes('red')) return 'background-color: #E3371E';
    return '';
  },

  updateCustomizeSelection(sectionId, fieldKey, rawValue, fieldType) {
    let value = rawValue;
    if (fieldType === 'toggle') {
      if (rawValue === 'true') value = true;
      else if (rawValue === 'false') value = false;
      else value = null;
    } else if (rawValue === '') {
      value = null;
    }

    const nextSection = { ...(this.customizeSelections[sectionId] || {}), [fieldKey]: value };
    this.customizeSelections = { ...this.customizeSelections, [sectionId]: nextSection };
    this.syncAllCustomizeSelections();
    this.updateCustomizePrices();
    this.fetchCustomizeProducts();
  },

  shouldShowCustomizeSection(sectionId) {
    if (sectionId === 'keyboard') {
      return this.getCustomizeEntries(sectionId).length > 0;
    }
    return true;
  },

  // Pricing helpers for customize modal
  getCustomizeMatchingEntry(sectionId) {
    const entries = this.getCustomizeEntries(sectionId);

    if (entries.length === 0) {
      return null;
    }

    if (entries.length === 1) {
      return entries[0];
    }

    // Multiple matches - prefer preload entry as default
    const preloadEntry = entries.find(e => e.preload === true);
    return preloadEntry || entries[0];
  },

  getCustomizeSectionPrice(sectionId, includeKeyboard = false) {
    const entry = this.getCustomizeMatchingEntry(sectionId);
    if (!entry) {
      return null;
    }

    let total = 0;
    // Only count products, not addons (addons are optional extras)
    const handles = entry.products || [];

    for (const handle of handles) {
      const product = this.products?.[handle];
      // Only add price if product is available (in stock)
      if (product?.price && product?.available !== false) {
        total += product.price;
      }
    }

    // Include keyboard price when showing mount section total
    if (sectionId === 'mount' && includeKeyboard) {
      const keyboardVisible = this.shouldShowCustomizeSection('keyboard');
      if (keyboardVisible) {
        const keyboardPrice = this.getCustomizeSectionPrice('keyboard', false);
        if (keyboardPrice !== null) {
          total += keyboardPrice;
        }
      }
    }

    return total;
  },

  getCustomizeSubtotal() {
    if (!this.customization?.sections) return null;

    let subtotal = 0;
    let hasPrice = false;

    for (const section of this.customization.sections) {
      // Skip keyboard - it's included in mount section price
      if (section.id === 'keyboard') {
        continue;
      }
      // For mount, include keyboard price
      const includeKeyboard = section.id === 'mount';
      const price = this.getCustomizeSectionPrice(section.id, includeKeyboard);
      if (price !== null) {
        subtotal += price;
        hasPrice = true;
      }
    }

    return hasPrice ? subtotal : null;
  },

  formatCustomizePrice(cents) {
    if (cents === null || cents === undefined) return '';
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  async fetchCustomizeProducts() {
    if (!this.customization?.sections) return;

    const handles = new Set();
    for (const section of this.customization.sections) {
      const entry = this.getCustomizeMatchingEntry(section.id);
      if (entry) {
        // Only fetch products, not addons (addons are optional extras not included in price)
        (entry.products || []).forEach(h => handles.add(h));
      }
    }

    const fetchPromises = [];
    for (const handle of handles) {
      if (!this.products?.[handle]) {
        fetchPromises.push(this.fetchProductData(handle));
      }
    }

    if (fetchPromises.length > 0) {
      await Promise.all(fetchPromises);
      // Trigger Alpine reactivity by creating new object references
      this.products = { ...this.products };
    }

    // Always update prices after fetch (even if no new products)
    this.updateCustomizePrices();
  },

  updateCustomizePrices() {
    if (!this.customization?.sections) {
      this.customizePrices = { subtotal: null };
      return;
    }

    const prices = {};
    let subtotal = 0;
    let hasPrice = false;

    for (const section of this.customization.sections) {
      if (section.id === 'keyboard') continue; // Skip - included in mount
      const includeKeyboard = section.id === 'mount';
      const price = this.getCustomizeSectionPrice(section.id, includeKeyboard);
      prices[section.id] = price;
      if (price !== null) {
        subtotal += price;
        hasPrice = true;
      }
    }

    prices.subtotal = hasPrice ? subtotal : null;
    this.customizePrices = prices;
  },

  buildCustomizeSectionContext(sectionId) {
    const section = this.getCustomizeSection(sectionId);
    if (!section) {
      return null;
    }

    const selections = this.customizeSelections?.[sectionId] || {};
    const sectionContext = {};

    section.fields?.forEach(field => {
      let value = selections[field.key];
      if (sectionId === 'case' && field.key === 'ergo_accessory' && (value === undefined || value === null || value === '')) {
        value = this.getErgoAccessoryNoneValue();
      }
      if (value === undefined || value === null || value === '') return;
      sectionContext[field.key] = value;
    });

    return Object.keys(sectionContext).length > 0 ? sectionContext : null;
  },

  async applyCustomization() {
    if (!this.canApplyCustomizeModal()) {
      this.customizeError = this.getCurrentCustomizeTarget() === 'laptop'
        ? 'Select a valid laptop style before continuing.'
        : 'Select a valid tablet case type before continuing.';
      return;
    }

    const target = this.getCurrentCustomizeTarget();
    const previousMainDeviceType = typeof this.getSelectedMainDeviceType === 'function'
      ? this.getSelectedMainDeviceType()
      : null;
    const previousContext = {
      tablet_make: this.context.tablet_make,
      tablet_model: this.context.tablet_model,
      tablet_case_type: this.context.tablet_case_type,
      tablet_mount: this.context.tablet_mount,
      tablet_case: this.context.tablet_case,
      tablet_dock: this.context.tablet_dock,
      tablet_keyboard: this.context.tablet_keyboard,
      laptop_selected: this.context.laptop_selected,
      laptop_style: this.context.laptop_style,
      other_device_selected: this.context.other_device_selected
    };
    const restoreContextValue = (key, value) => {
      if (value === undefined) {
        delete this.context[key];
        return;
      }
      this.context[key] = value;
    };
    let prunedAccessories = [];
    const shouldAdvanceFromStep2 = Number(this.step || 2) === 2;
    this.customizeLoading = true;
    this.customizeError = null;

    try {
      if (target === 'laptop') {
        const laptop = this.buildCustomizeSectionContext('laptop');
        if (previousMainDeviceType && previousMainDeviceType !== 'laptop' && typeof this.pruneAccessoriesForContextChange === 'function') {
          prunedAccessories = this.pruneAccessoriesForContextChange(['main_device', 'tablet_selection']);
        }

        this.resetTabletContext();
        delete this.context.other_device_selected;
        this.context.laptop_selected = true;

        if (laptop?.style) this.context.laptop_style = laptop.style;
        else delete this.context.laptop_style;

        await this.api({
          current_step: this.step || 2,
          proceed: shouldAdvanceFromStep2
        });
        if (this.error) {
          throw new Error(this.error);
        }

        this.localKit = {
          laptop: this.response.kit?.laptop || { products: [], images: [], style: this.context.laptop_style || null },
          tablet: { products: [], images: [] },
          other_device: { products: [], images: [] }
        };

        if (this.step >= 2 && typeof this.syncSnapshotWithCurrentState === 'function') {
          try {
            await this.syncSnapshotWithCurrentState(true);
          } catch (snapshotError) {
            console.warn('[Tough-Bar Customize] Snapshot refresh failed:', snapshotError);
          }
        }

        if (shouldAdvanceFromStep2 && this.step >= 3 && typeof this.clearStepSnapshotId === 'function') {
          this.clearStepSnapshotId(2);
        }

        const laptopKit = this.localKit.laptop;
        if (laptopKit?.products?.length > 0) {
          const fetchPromises = laptopKit.products.map(handle => this.fetchProductData(handle));
          await Promise.all(fetchPromises);
        }

        this.customizeApplied = true;
        this.closeModal();
        return;
      }

      const mount = this.buildCustomizeSectionContext('mount');
      const tabletCase = this.buildCustomizeSectionContext('case');
      const dock = this.buildCustomizeSectionContext('dock');
      const keyboard = this.buildCustomizeSectionContext('keyboard');
      if (previousMainDeviceType && previousMainDeviceType !== 'tablet' && typeof this.pruneAccessoriesForContextChange === 'function') {
        prunedAccessories = this.pruneAccessoriesForContextChange(['main_device', 'tablet_selection']);
      }

      if (mount) this.context.tablet_mount = mount;
      else delete this.context.tablet_mount;

      if (tabletCase) this.context.tablet_case = tabletCase;
      else delete this.context.tablet_case;

      if (dock) this.context.tablet_dock = dock;
      else delete this.context.tablet_dock;

      if (keyboard) this.context.tablet_keyboard = keyboard;
      else this.clearTabletKeyboardContext();

      this.resetLaptopContext();
      delete this.context.other_device_selected;

      await this.api({
        current_step: this.step || 2,
        proceed: shouldAdvanceFromStep2
      });
      if (this.error) {
        throw new Error(this.error);
      }

      const confirmedSelection = {
        tablet_make: this.context.tablet_make || null,
        tablet_model: this.context.tablet_model || null,
        tablet_case_type: this.context.tablet_case_type || null
      };
      this.tabletSelectionSignature = this.getTabletSelectionSignature();
      this.tabletSelectionConfirmed = confirmedSelection;
      this.customizeTabletSignature = this.tabletSelectionSignature;

      this.localKit = {
        tablet: this.response.kit?.tablet || { products: [], images: [] },
        laptop: { products: [], images: [] },
        other_device: { products: [], images: [] }
      };

      if (this.step >= 2 && typeof this.syncSnapshotWithCurrentState === 'function') {
        try {
          await this.syncSnapshotWithCurrentState(true);
        } catch (snapshotError) {
          console.warn('[Tough-Bar Customize] Snapshot refresh failed:', snapshotError);
        }
      }

      if (shouldAdvanceFromStep2 && this.step >= 3 && typeof this.clearStepSnapshotId === 'function') {
        this.clearStepSnapshotId(2);
      }

      // Fetch product data for the updated tablet kit
      const tabletKit = this.localKit.tablet;
      if (tabletKit?.products?.length > 0) {
        const fetchPromises = tabletKit.products.map(handle => this.fetchProductData(handle));
        await Promise.all(fetchPromises);
      }

      this.customizeApplied = true;
      this.closeModal();
    } catch (err) {
      Object.entries(previousContext).forEach(([key, value]) => {
        restoreContextValue(key, value);
      });
      if (prunedAccessories.length > 0 && typeof this.restorePrunedAccessories === 'function') {
        this.restorePrunedAccessories(prunedAccessories);
      }
      console.error('[Tough-Bar Customize] Failed to apply customization:', err);
      this.customizeError = 'Failed to apply customization. Please try again.';
    } finally {
      this.customizeLoading = false;
    }
  },

  // Notify Modal
  openNotifyModal(handle) {
    this.notifySuccess = false;
    this.notifyEmailError = false;
    this.notifyLoading = false;
    this.openModal('notify', { handle });
  },

  async submitNotify() {
    const email = this.notifyEmail.trim();
    const isValidEmail = /^[A-Za-z0-9_!#$%&'*+\/=?`{|}~^.-]+@[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/.test(email);

    if (!isValidEmail) {
      this.notifyEmailError = true;
      return;
    }

    this.notifyEmailError = false;
    this.notifyLoading = true;

    const product = this.notifyProduct;
    if (!product) {
      this.notifyLoading = false;
      this.error = 'Product data not available';
      return;
    }

    try {
      const response = await fetch('https://app.restockrocket.io/api/v1/intents.json', {
        method: 'POST',
        headers: {
          'X-Shopify-Shop-Domain': 'rammount.myshopify.com',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intent: {
            shopify_variant_id: product.variants?.[0]?.id,
            shopify_product_id: product.id,
            channel: 'email',
            quantity: 1,
            source: 'api'
          },
          customer: {
            email: email,
            accepts_marketing: this.notifyMarketing
          },
          product: {
            title: product.title,
            variant_count: product.variants?.length || 1,
            variant_title: product.title
          }
        })
      });

      if (response.ok) {
        this.notifySuccess = true;
      } else {
        throw new Error('Failed to submit notification request');
      }
    } catch (err) {
      console.error('Notify request failed:', err);
      this.error = 'Failed to submit notification. Please try again.';
    } finally {
      this.notifyLoading = false;
    }
  },

  resetNotifyState() {
    this.notifyEmail = '';
    this.notifyMarketing = true;
    this.notifyLoading = false;
    this.notifySuccess = false;
    this.notifyEmailError = false;
  },

  // Preview Modal
  initPreviewModal() {
    if (!this.previewModal && window.ToughBarPreviewModal) {
      this.previewModal = new window.ToughBarPreviewModal();
    }
  },

  getPreviewDrawerHandoffDelay() {
    return 220;
  },

  _isPreviewLaunchingFromMobileDrawer() {
    if (!this.sideCartDrawerOpen) return false;
    if (typeof this._isDesktopSideCartViewport === 'function') {
      return !this._isDesktopSideCartViewport();
    }
    return window.innerWidth < 768;
  },

  _shouldRestoreDrawerAfterPreviewClose() {
    if (typeof this._isDesktopSideCartViewport === 'function' && this._isDesktopSideCartViewport()) {
      return false;
    }
    return Boolean(this.showPricing && typeof this.openSideCartDrawer === 'function');
  },

  _clearPendingPreviewDrawerHandoff() {
    if (!this._previewDrawerHandoffTimer) return;
    window.clearTimeout(this._previewDrawerHandoffTimer);
    this._previewDrawerHandoffTimer = undefined;
  },

  async openPreviewModal(handle, event = null) {
    if (!handle) return;

    // Initialize preview modal if not already done
    this.initPreviewModal();
    this._clearPendingPreviewDrawerHandoff();

    if (this.previewModal) {
      const triggerElement = event?.currentTarget || null;
      const launchedFromMobileDrawer = this._isPreviewLaunchingFromMobileDrawer();
      const previewOptions = launchedFromMobileDrawer
        ? {
            onAfterClose: () => {
              if (!this._shouldRestoreDrawerAfterPreviewClose()) return;
              if (!this.sideCartDrawerOpen) {
                this.openSideCartDrawer();
              }
            }
          }
        : {};

      if (!launchedFromMobileDrawer) {
        await this.previewModal.open(handle, triggerElement, previewOptions);
        return;
      }

      if (typeof this.closeSideCartDrawer === 'function') {
        this.closeSideCartDrawer();
      } else {
        this.sideCartDrawerOpen = false;
      }

      this._previewDrawerHandoffTimer = window.setTimeout(async () => {
        this._previewDrawerHandoffTimer = undefined;
        const opened = await this.previewModal.open(handle, triggerElement, previewOptions);
        if (!opened && this._shouldRestoreDrawerAfterPreviewClose() && !this.sideCartDrawerOpen) {
          this.openSideCartDrawer();
        }
      }, this.getPreviewDrawerHandoffDelay());
    } else {
      console.warn('[Tough-Bar] Preview modal not available');
    }
  },
};
